import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const MOBILE_QUERY = '(max-width: 900px), (pointer: coarse)';

function isVisible(element: Element) {
  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || 1) > 0
  );
}

function findMemoryViewerRoot(): HTMLElement | null {
  const memoryImages = Array.from(
    document.querySelectorAll<HTMLImageElement>(
      'img[src*="/images/memory-"], img[src*="memory-"]',
    ),
  );

  for (const image of memoryImages) {
    if (!isVisible(image)) continue;

    const imageRect = image.getBoundingClientRect();

    // Ignore the ordinary gallery cards. The viewer image is large on screen.
    if (
      imageRect.width < Math.min(220, window.innerWidth * 0.55) ||
      imageRect.height < Math.min(260, window.innerHeight * 0.45)
    ) {
      continue;
    }

    let current: HTMLElement | null = image.parentElement;

    while (current && current !== document.body) {
      const className =
        typeof current.className === 'string'
          ? current.className.toLowerCase()
          : '';
      const role = current.getAttribute('role')?.toLowerCase();
      const style = window.getComputedStyle(current);
      const rect = current.getBoundingClientRect();

      const modalClass =
        className.includes('modal') ||
        className.includes('viewer') ||
        className.includes('lightbox') ||
        className.includes('fullscreen');

      const coversViewport =
        rect.width >= window.innerWidth * 0.78 &&
        rect.height >= window.innerHeight * 0.72;

      if (
        role === 'dialog' ||
        modalClass ||
        (style.position === 'fixed' && coversViewport)
      ) {
        return current;
      }

      current = current.parentElement;
    }
  }

  return null;
}

function calculateTopOffset() {
  const navCandidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      '.floating-nav, .mobile-nav, header nav, body > nav',
    ),
  ).filter(isVisible);

  const topNav = navCandidates.find((nav) => {
    const rect = nav.getBoundingClientRect();
    return rect.top < 45 && rect.height < 130;
  });

  if (!topNav) return 18;

  return Math.min(Math.max(topNav.getBoundingClientRect().bottom + 12, 18), 122);
}

export function MemoryViewerClose() {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [top, setTop] = useState(18);
  const viewerRootRef = useRef<HTMLElement | null>(null);

  const inspectPage = useCallback(() => {
    const root = findMemoryViewerRoot();
    viewerRootRef.current = root;
    setViewerOpen(Boolean(root));
    setTop(calculateTopOffset());
  }, []);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);

    const updateMobile = () => {
      setMobile(media.matches);
      inspectPage();
    };

    updateMobile();
    media.addEventListener?.('change', updateMobile);

    const observer = new MutationObserver(inspectPage);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-hidden'],
    });

    window.addEventListener('resize', inspectPage);
    window.addEventListener('orientationchange', inspectPage);

    const interval = window.setInterval(inspectPage, 300);

    return () => {
      media.removeEventListener?.('change', updateMobile);
      observer.disconnect();
      window.removeEventListener('resize', inspectPage);
      window.removeEventListener('orientationchange', inspectPage);
      window.clearInterval(interval);
    };
  }, [inspectPage]);

  const closeViewer = () => {
    const root = viewerRootRef.current ?? findMemoryViewerRoot();

    const existingClose = root?.querySelector<HTMLElement>(
      [
        'button[aria-label*="close" i]',
        'button[title*="close" i]',
        '.modal-close',
        '.viewer-close',
        '.lightbox-close',
        '.close-button',
      ].join(','),
    );

    if (existingClose && isVisible(existingClose)) {
      existingClose.click();
      return;
    }

    const escapeEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      bubbles: true,
      cancelable: true,
    });

    document.dispatchEvent(escapeEvent);
    window.dispatchEvent(escapeEvent);

    window.setTimeout(() => {
      const stillOpen = findMemoryViewerRoot();

      if (!stillOpen) return;

      // Fallback for viewers that close when the backdrop itself is clicked.
      stillOpen.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: 4,
          clientY: 4,
        }),
      );
    }, 120);
  };

  return (
    <AnimatePresence>
      {mobile && viewerOpen && (
        <motion.button
          type="button"
          className="mobile-memory-viewer-close"
          aria-label="Close photo viewer"
          title="Close photo"
          onClick={closeViewer}
          style={{
            position: 'fixed',
            top,
            right: 14,
            width: 50,
            height: 50,
            minWidth: 50,
            minHeight: 50,
            padding: 0,
            display: 'grid',
            placeItems: 'center',
            border: '2px solid rgba(255,255,255,.92)',
            borderRadius: '999px',
            color: '#fff',
            background:
              'linear-gradient(135deg, #ff4fa3 0%, #c846e7 58%, #8546dd 100%)',
            boxShadow:
              '0 12px 30px rgba(104,31,91,.32), 0 0 0 6px rgba(255,255,255,.18)',
            zIndex: 2147483000,
            cursor: 'pointer',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
          initial={{ opacity: 0, scale: 0.45, rotate: -135, y: -16 }}
          animate={{
            opacity: 1,
            scale: 1,
            rotate: 0,
            y: 0,
            boxShadow: [
              '0 12px 30px rgba(104,31,91,.32), 0 0 0 4px rgba(255,255,255,.14)',
              '0 12px 32px rgba(104,31,91,.38), 0 0 0 9px rgba(255,79,163,.16)',
              '0 12px 30px rgba(104,31,91,.32), 0 0 0 4px rgba(255,255,255,.14)',
            ],
          }}
          exit={{ opacity: 0, scale: 0.35, rotate: 135, y: -14 }}
          transition={{
            opacity: { duration: 0.18 },
            scale: { type: 'spring', stiffness: 380, damping: 20 },
            rotate: { type: 'spring', stiffness: 300, damping: 20 },
            boxShadow: { duration: 1.8, repeat: Infinity },
          }}
          whileTap={{ scale: 0.82, rotate: 90 }}
        >
          <X size={28} strokeWidth={3} aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}